import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect when an element enters the viewport for the first time.
 *
 * Behavior:
 * - Returns a callback ref to be assigned to the target element.
 * - `isVisible` becomes true the first time the element intersects the viewport by the given threshold.
 * - After the first intersection, `isVisible` stays true even if the element leaves the viewport.
 * - If the target element changes, the state resets and detection starts over.
 *
 * @param options IntersectionObserver options (e.g., threshold).
 *   - `threshold` defines what percentage of the target's visibility
 *     triggers the observer callback. For example:
 *       - 0.0 means any pixel visible triggers the callback,
 *       - 1.0 means the entire element must be visible,
 *       - 0.1 means 10% of the element should be visible to trigger.
 * @returns [refCallback, isVisible] - A callback ref to attach to the element, and a boolean indicating visibility.
 */
export const useElementOnScreen = (
  options: IntersectionObserverInit = { threshold: 0.1 },
): [(node: HTMLDivElement | null) => void, boolean] => {
  // State to track whether the element has been visible at least once
  const [isVisible, setIsVisible] = useState(false);

  // Ref to store the current observed element
  const elementRef = useRef<HTMLDivElement | null>(null);

  // Ref to track if the element has already intersected once
  const hasEnteredOnce = useRef(false);

  // Reset visibility state and tracking flag when the observed element changes
  const resetState = useCallback(() => {
    hasEnteredOnce.current = false;
    setIsVisible(false);
  }, []);

  /**
   * Callback ref function to be passed to the element.
   * Called by React when the element mounts, unmounts or changes.
   */
  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      // If the element has changed, reset the state
      if (elementRef.current !== node) {
        elementRef.current = node;
        resetState();
      }
    },
    [resetState],
  );

  /**
   * IntersectionObserver callback that triggers when the element intersects the viewport.
   */
  const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    const entry = entries[0];
    if (!entry) return;

    // When the element is intersecting and it hasn't been seen before, set visibility to true
    if (entry.isIntersecting && !hasEnteredOnce.current) {
      setIsVisible(true);
      hasEnteredOnce.current = true;
    }
    // Note: We intentionally do NOT set isVisible to false when the element leaves the viewport,
    // so the visibility stays true once entered.
  }, []);

  // Setup IntersectionObserver on the element whenever options or callback change
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(observerCallback, options);
    observer.observe(element);

    // Cleanup observer on unmount or when dependencies change
    return () => {
      observer.disconnect();
    };
  }, [options, observerCallback]);

  // Return the callback ref to assign to the element, and the visibility state
  return [refCallback, isVisible];
};
